import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { libraryError, libraryFileUrl, libraryHeaders, librarySessionExpired } from '../lib/libraryFileAccess';

const TEXT_MIMES = new Set(['text/plain', 'text/csv', 'text/markdown', 'application/json', 'application/xml']);

export const previewKind = (file) => {
  const mime = (file.mime_type || '').toLowerCase().split(';')[0];
  if (mime.startsWith('image/')) return 'image';
  if (mime === 'application/pdf') return 'pdf';
  if (TEXT_MIMES.has(mime)) return 'text';
  if (/\.(png|jpe?g|gif|webp)$/i.test(file.filename || '')) return 'image';
  if (/\.pdf$/i.test(file.filename || '')) return 'pdf';
  if (/\.(txt|csv|md|json|xml|log)$/i.test(file.filename || '')) return 'text';
  return 'unsupported';
};

export const useLibraryFilePreview = (file) => {
  const kind = previewKind(file);
  const [page, setPage] = useState(1);
  const url = libraryFileUrl(file.id, kind === 'image' ? 'preview' : kind === 'pdf' ? `pdf-preview?page=${page}` : 'download');
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState({ status: 'loading', blobUrl: null, text: '', error: '', pages: 0 });
  const retry = useCallback(() => setAttempt((n) => n + 1), []);
  const loaded = useCallback((event) => {
    const src = event.currentTarget.src;
    setState((old) => old.status === 'loading' && old.blobUrl === src ? { ...old, status: 'ready' } : old);
  }, []);
  const failed = useCallback((event) => {
    const src = event.currentTarget.src;
    setState((old) => old.blobUrl === src ? { ...old, status: 'error', error: 'Your browser could not display this preview. Please retry or download the original.' } : old);
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    let createdUrl = null;
    let decodeTimer = null;
    setState((old) => ({ ...old, status: 'loading', blobUrl: null, text: '', error: '' }));
    (async () => {
      try {
        const headers = libraryHeaders();
        if (kind === 'unsupported') { setState({ status: 'unsupported', blobUrl: null, text: '', error: '', pages: 0 }); return; }
        const response = await axios.get(url, {
          headers,
          responseType: kind === 'text' ? 'text' : 'blob',
          signal: controller.signal, timeout: 60000,
        });
        if (controller.signal.aborted) return;
        if (kind === 'text') {
          setState({ status: 'ready', blobUrl: null, text: typeof response.data === 'string' ? response.data : JSON.stringify(response.data, null, 2), error: '', pages: 0 });
        } else {
          const pages = kind === 'pdf' ? Number(response.headers['x-pdf-pages']) : 0;
          if (kind === 'pdf' && (!Number.isInteger(pages) || pages < 1)) throw new Error('PDF page information is missing');
          const mime = response.headers['content-type'] || 'image/jpeg';
          createdUrl = URL.createObjectURL(new Blob([response.data], { type: mime }));
          setState({ status: 'loading', blobUrl: createdUrl, text: '', error: '', pages });
          decodeTimer = setTimeout(() => {
            if (!controller.signal.aborted) setState((old) => old.status === 'loading'
              ? { ...old, status: 'error', error: 'The preview did not finish displaying. Please retry or download the original.' } : old);
          }, 20000);
        }
      } catch (error) {
        if (!controller.signal.aborted) {
          const message = await libraryError(error);
          if (!controller.signal.aborted) setState({ status: librarySessionExpired(error) ? 'expired' : 'error', blobUrl: null, text: '', error: message, pages: 0 });
        }
      }
    })();
    return () => { controller.abort(); clearTimeout(decodeTimer); if (createdUrl) URL.revokeObjectURL(createdUrl); };
  }, [kind, url, attempt]);

  return { ...state, kind, page, setPage, retry, loaded, failed };
};